import {
  Placeholder,
  PlaceholderGroupDataType,
  PlaceholderGroupMember,
  PlaceholderGroupValidations,
  PluginEnableContext,
  PluginExperimentContext,
} from "./PluginRegistryTypes";
import PluginRegistry from "./PluginRegistry";

export interface PlaceholderGroupValidationResult {
  passed: boolean;
  validationsRun: ValidationRun[];
}

export interface ValidationRun {
  type: PlaceholderGroupValidations;
  passed: boolean;
  details: Record<string, string[]>;
}

export interface ValidationResult {
  passed: boolean;
  details: Record<string, string[]>;
}

export const placeholderGroupValidationDescription: Record<PlaceholderGroupValidations, string> = {
  [PlaceholderGroupValidations.AllPlaceholdersFilled]:
    "Each placeholder in this group needs to have some components rendered",
  [PlaceholderGroupValidations.AtLeastOnePlaceholderRendered]:
    "At least one placeholder in this group needs to have some components rendered",
};

export const validatePlaceholdersGroup = (props: {
  pluginRegistry: PluginRegistry;
  enableContext: PluginEnableContext;
  parentComponents: string[];
  dataGroup: PlaceholderGroupDataType<any>[];
  validationTypes: PlaceholderGroupValidations[];
  pluginOrdering?: { [key in Placeholder | PlaceholderGroupMember]?: string[][] };
  useExperiment: (context: PluginExperimentContext) => string | undefined;
}): PlaceholderGroupValidationResult => {
  const { validationTypes } = props;
  const validationResult: PlaceholderGroupValidationResult = { passed: true, validationsRun: [] };

  if (validationTypes.includes(PlaceholderGroupValidations.AllPlaceholdersFilled)) {
    const result = validateAllPlaceholderFilled(props);
    validationResult.validationsRun.push({
      type: PlaceholderGroupValidations.AllPlaceholdersFilled,
      ...result,
    });
  }

  if (validationTypes.includes(PlaceholderGroupValidations.AtLeastOnePlaceholderRendered)) {
    const result = validateAtLeastOnePlaceholderRendered(props);
    validationResult.validationsRun.push({
      type: PlaceholderGroupValidations.AtLeastOnePlaceholderRendered,
      ...result,
    });
  }

  return {
    ...validationResult,
    passed: validationResult.validationsRun.every(run => run.passed),
  };
};

const validateAllPlaceholderFilled = (props: {
  pluginRegistry: PluginRegistry;
  enableContext: PluginEnableContext;
  parentComponents: string[];
  dataGroup: PlaceholderGroupDataType<any>[];
  pluginOrdering?: { [key in Placeholder | PlaceholderGroupMember]?: string[][] };
  useExperiment: (context: PluginExperimentContext) => string | undefined;
}): ValidationResult => {
  const {
    pluginRegistry,
    enableContext,
    parentComponents,
    dataGroup,
    pluginOrdering,
    useExperiment,
  } = props;
  const validationResults: Record<string, string[]> = {};
  const placeholdersFilled = dataGroup.map(data => {
    const orderedComponents = pluginOrdering?.[data.type];
    const components = pluginRegistry.getComponents(
      useExperiment,
      data.type,
      enableContext,
      parentComponents,
      orderedComponents
    );
    validationResults[data.type] = components.map(component => component.name);
    return components.length > 0;
  });
  const passed = placeholdersFilled.every(result => result);
  return { passed, details: validationResults };
};

const validateAtLeastOnePlaceholderRendered = (props: {
  pluginRegistry: PluginRegistry;
  enableContext: PluginEnableContext;
  parentComponents: string[];
  dataGroup: PlaceholderGroupDataType<any>[];
  pluginOrdering?: { [key in Placeholder | PlaceholderGroupMember]?: string[][] };
  useExperiment: (context: PluginExperimentContext) => string | undefined;
}): ValidationResult => {
  const {
    pluginRegistry,
    enableContext,
    parentComponents,
    dataGroup,
    pluginOrdering,
    useExperiment,
  } = props;
  const validationResults: Record<string, string[]> = {};
  const placeholdersFilled = dataGroup.map(data => {
    const orderedComponents = pluginOrdering?.[data.type];
    const components = pluginRegistry.getComponents(
      useExperiment,
      data.type,
      enableContext,
      parentComponents,
      orderedComponents
    );
    validationResults[data.type] = components.map(component => component.name);
    return components.length > 0;
  });
  const passed = placeholdersFilled.some(result => result);
  return { passed, details: validationResults };
};
